import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { User } from '../dtos/web-user';
import { environment } from '../../environments/environment';
import { BaseResponse } from '../dtos/base-response';
import { ListResponse } from '../dtos/list-response';
import { ListRequest } from '../dtos/list-request';
import { PasswordChange } from '../dtos/password-change';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;
  public appVersionSubject: BehaviorSubject<string>;

  constructor(private http: HttpClient) {
    this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentUser')!));
    this.currentUser = this.currentUserSubject.asObservable();
    this.appVersionSubject = new BehaviorSubject<string>('v1.0.0');
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  public get appVersion(): string {
    return this.appVersionSubject.value;
  }

  login(userMailId: string, password: string) {
    return this.http.post<User>(`${environment.apiBaseUrl}auth/login`, { userMailId, password }).pipe(
      map(user => {
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.currentUserSubject.next(user);
        return user;
      })
    );
  }

  getUsers(request: ListRequest): Observable<BaseResponse<ListResponse<User>>> {
    return this.http.post<BaseResponse<ListResponse<User>>>(`${environment.apiBaseUrl}auth/users/search`, request);
  }

  logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null as unknown as User);
  }

  register(user: User) {
    return this.http.post<any>(`${environment.apiBaseUrl}auth/register`, user).pipe(
      map(user => {
        return user;
      })
    );
  }

  createOrUpdateUser(user: User) {
    return this.http.post<User>(`${environment.apiBaseUrl}auth/add-or-update-user`, user);
  }

  getUserById(userId: number): Observable<User> {
    return this.http.get<User>(`${environment.apiBaseUrl}auth/get-user-by-id/${userId}`);
  }

  resetPassword(user: User) {
    return this.http.post<any>(`${environment.apiBaseUrl}auth/reset-password`, user).pipe(
      map(user => {
        return user;
      })
    );
  }

  changePassword(changePassword: PasswordChange) {
    return this.http.post<any>(`${environment.apiBaseUrl}auth/user/change-password`, changePassword).pipe();
  }

  deleteUser(id: number) {
    return this.http.delete<any>(`${environment.apiBaseUrl}auth/delete-user/${id}`);
  }

  sendForgotPasswordLink(userName: string) {
    return this.http.post<any>(`${environment.apiBaseUrl}auth/forgot-password`, { userName });
  }

  validatePasswordResetToken(token: string) {
    return this.http.get<PasswordChange>(`${environment.apiBaseUrl}auth/user/validate-password-reset-token/${token}`);
  }

  resetPasswordWithToken(changePassword: PasswordChange) {
    return this.http.post<any>(`${environment.apiBaseUrl}auth/user/reset-password-with-token`, changePassword).pipe();
  }
}
